import { Controller } from '@hotwired/stimulus'

/**
 * Client-side CSV export for visible drill-down table data.
 * Scrapes the DOM table — what you see is what you get.
 *
 * Each drill-down dimension gets its own column in the CSV. The badge text
 * (e.g. "Campaign", "Publisher", "Call Buyer", "Revenue", "Daily") determines which column
 * the row's value is placed into. Other dimension columns are left blank.
 *
 * Example output:
 *   Campaign,Publisher,Call Buyer,Total,Revenue
 *   1ee94a75 - Test dev Campaign123,,,118,"$505,298"
 *   ,3333 - Test Test,,34,"$15,391"
 *   ,,No Call Buyer,8,"$58,114"
 *
 * Usage:
 *   <section data-controller="drill-down-table-csv-export">
 *     <button data-action="click->drill-down-table-csv-export#export">CSV Export</button>
 *     <table data-drill-down-table-csv-export-target="table">...</table>
 *   </section>
 */
export default class extends Controller {
    static targets = ['table']

    export() {
        const csv = this.buildCsv(this.tableTarget)
        this.download(csv)
    }

    buildCsv(table) {
        const bodyRows = table.querySelectorAll('tbody tr')

        // First pass: collect unique dimension names in order of first appearance
        const dimensionNames = []
        bodyRows.forEach((tr) => {
            const badge = tr.querySelector('td .badge')
            if (badge) {
                const name = badge.textContent.trim()
                if (!dimensionNames.includes(name)) {
                    dimensionNames.push(name)
                }
            }
        })

        // Collect visible fact column headers (skip the first <th> — it's the dimension column)
        const headerCells = table.querySelectorAll('thead th')
        const factHeaders = []
        const visibleFactIndices = new Set()

        headerCells.forEach((th, index) => {
            if (index === 0 || this.isHidden(th)) return
            factHeaders.push(this.escapeCsvValue(th.textContent.trim()))
            visibleFactIndices.add(index)
        })

        // Build header row: Type + dimension columns + fact columns
        const csvRows = []
        csvRows.push(['Type', ...dimensionNames.map((n) => this.escapeCsvValue(n)), ...factHeaders].join(','))

        // Second pass: build each data row
        bodyRows.forEach((tr) => {
            const cells = tr.querySelectorAll('td')

            const firstCell = cells[0]
            const badge = firstCell.querySelector('.badge')

            if (!badge) {
                // Non-data row (e.g. "Showing 3 results. Load more...")
                const text = firstCell.textContent.trim()
                const totalCols = dimensionNames.length + factHeaders.length + 1 // +1 for Type column
                csvRows.push(this.escapeCsvValue(text) + ','.repeat(totalCols - 1))
                return
            }

            const badgeText = badge.textContent.trim()
            const valueText = this.getFirstCellValue(firstCell)

            // Type column + value in the matching dimension column, leave others blank
            const dimensionCols = [this.escapeCsvValue(badgeText), ...dimensionNames.map((name) => (name === badgeText ? this.escapeCsvValue(valueText) : ''))]

            // Fact columns
            const factCols = []
            cells.forEach((td, index) => {
                if (index === 0) return
                if (visibleFactIndices.has(index)) {
                    factCols.push(this.escapeCsvValue(td.textContent.trim()))
                }
            })

            csvRows.push([...dimensionCols, ...factCols].join(','))
        })

        return csvRows.join('\n')
    }

    /**
     * Extract only bare text nodes from the first cell, removing all the spans as they contain icons and chevrons
     */
    getFirstCellValue(cell) {
        const clonedCell = cell.cloneNode(true)
        clonedCell.querySelectorAll('span').forEach((el) => el.remove())
        return clonedCell.textContent.trim()
    }

    escapeCsvValue(value) {
        if (/^[=+\-@\t]/.test(value)) {
            value = "'" + value   // leading apostrophe disables formula execution
        }
        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
            return '"' + value.replace(/"/g, '""') + '"'
        }
        return value
    }

    isHidden(element) {
        // # If we try to export an edited, but not saved template
        return element.classList.contains('d-none')
    }

    download(csv) {
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const date = new Date().toISOString().slice(0, 10)

        const link = document.createElement('a')
        link.href = url
        link.download = `drill_down_export_${date}.csv`
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()

        document.body.removeChild(link)
        URL.revokeObjectURL(url)
    }
}
